import React, { useState, useEffect } from 'react';
import { Calculator, Loader2, TrendingUp, Info, Truck } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import LocationAutocomplete from '../components/LocationAutocomplete';
import MultiProviderMap from '../components/MultiProviderMap';

const SimulationOperateur = () => {
  const [formData, setFormData] = useState({
    tonnage_max: 25,
    ville_depart: '',
    ville_destination: '',
    distance_km: '',
    type_marchandise: 'standard',
    conditionnement: 'carrosserie',
    retour_vide: false,
    urgence: 'normal',
    profil_client: 'standard',
    marge_minimale: 22
  });

  const [loading, setLoading] = useState(false);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Variables pour la carte multi-provider (OSM/Mapbox)
  const [originLocation, setOriginLocation] = useState(null);
  const [destinationLocation, setDestinationLocation] = useState(null);
  const [calculatedDistance, setCalculatedDistance] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);

  // Calcul automatique distance avec le système multi-provider
  useEffect(() => {
    const fetchDistance = async () => {
      if (originLocation && destinationLocation) {
        setDistanceLoading(true);
        try {
          const response = await api.post('/geo/distance', {
            origin: {
              lat: originLocation.latitude,
              lng: originLocation.longitude
            },
            destination: {
              lat: destinationLocation.latitude,
              lng: destinationLocation.longitude
            },
            profile: 'driving'
          });
          
          if (response.data.success) {
            const distanceData = response.data;
            setCalculatedDistance(distanceData);
            setRouteCoordinates(distanceData.coordinates || []);
            setFormData(prev => ({ 
              ...prev, 
              distance_km: distanceData.distance_km 
            }));
            toast.success(`Distance calculée: ${distanceData.distance_km} km (${distanceData.duration_hours}) via ${distanceData.provider}`);
          }
        } catch (err) {
          console.error('Erreur calcul distance:', err);
          toast.error('Impossible de calculer la distance automatiquement');
        } finally {
          setDistanceLoading(false);
        }
      }
    };

    fetchDistance();
  }, [originLocation, destinationLocation]);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const payload = {
        tonnage_max: parseFloat(formData.tonnage_max),
        ville_depart: formData.ville_depart,
        ville_destination: formData.ville_destination,
        distance_km: formData.distance_km ? parseFloat(formData.distance_km) : null,
        type_marchandise: formData.type_marchandise,
        conditionnement: formData.conditionnement,
        retour_vide: formData.retour_vide,
        urgence: formData.urgence,
        profil_client: formData.profil_client,
        marge_minimale: formData.marge_minimale / 100,
        include_historical: true
      };

      const response = await api.post('/pricing/calculate', payload);
      setResult(response.data);
      toast.success('Tarif calculé avec succès !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du calcul');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(value));
  };

  return (
    <div className="h-screen bg-gray-50 p-3 overflow-hidden">
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        {/* Header compact */}
        <div className="mb-3">
          <h1 className="text-2xl font-bold mb-1">Simulateur de Tarification - Opérateur</h1>
          <p className="text-sm text-gray-600">Calcul détaillé du tarif avec carte et analyse</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 flex-1 overflow-hidden">
          {/* Formulaire - Gauche (1/3) */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-4 overflow-y-auto">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2 sticky top-0 bg-white pb-2 border-b">
              <Truck className="w-5 h-5 text-emerald-600" />
              Paramètres de l'opération
            </h2>

            {/* Tonnage */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tonnage maximum (tonnes)</label>
              <input
                type="number"
                value={formData.tonnage_max}
                onChange={(e) => setFormData({ ...formData, tonnage_max: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                step="0.5"
                min="0"
              />
            </div>

            {/* Villes avec autocomplete multi-provider (Zone CEDEAO) */}
            <div className="mb-2.5">
              <LocationAutocomplete
                label="Ville de départ"
                placeholder="Rechercher un lieu de départ..."
                value={formData.ville_depart}
                onLocationSelect={(location) => {
                  setOriginLocation(location);
                  setFormData(prev => ({ ...prev, ville_depart: location.shortName || location.label }));
                }}
                country=""
              />
            </div>

            <div className="mb-2.5">
              <LocationAutocomplete
                label="Ville de destination"
                placeholder="Rechercher un lieu de destination..."
                value={formData.ville_destination}
                onLocationSelect={(location) => {
                  setDestinationLocation(location);
                  setFormData(prev => ({ ...prev, ville_destination: location.shortName || location.label }));
                }}
                country=""
              />
            </div>

            {/* Distance */}
            <div className="mb-2.5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Distance (km)</label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.distance_km}
                  onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Calculée automatiquement"
                  min="0"
                />
                {distanceLoading && (
                  <Loader2 className="absolute right-3 top-2.5 w-5 h-5 animate-spin text-gray-400" />
                )}
              </div>
              {calculatedDistance && (
                <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded-md">
                  <p className="text-xs text-emerald-700">
                    ✓ Distance: <span className="font-semibold">{calculatedDistance.distance_km} km</span> • 
                    Durée: <span className="font-semibold">{calculatedDistance.duration_hours}</span>
                    <span className="ml-2 px-1.5 py-0.5 bg-emerald-200 rounded text-xs">
                      {calculatedDistance.provider === 'openstreetmap' ? 'OSM' : 'Mapbox'}
                    </span>
                  </p>
                </div>
              )}
              {!calculatedDistance && formData.ville_depart && formData.ville_destination && !originLocation && !destinationLocation && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-xs text-amber-700">
                    ⚠️ Pour calculer automatiquement la distance, veuillez <span className="font-semibold">sélectionner les villes depuis les suggestions</span> qui apparaissent quand vous tapez (icône loupe).
                  </p>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">Automatiquement calculé après sélection des lieux depuis l'autocomplete</p>
            </div>

            <hr className="my-4" />

            {/* Type marchandise */}
            <div className="mb-2.5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Type de marchandise</label>
              <select
                value={formData.type_marchandise}
                onChange={(e) => setFormData({ ...formData, type_marchandise: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="standard">Standard</option>
                <option value="fragile">Fragile</option>
                <option value="valeur">Valeur</option>
                <option value="chimique">Chimique</option>
                <option value="carburant">Carburant</option>
              </select>
            </div>

            {/* Conditionnement */}
            <div className="mb-2.5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Conditionnement</label>
              <select
                value={formData.conditionnement}
                onChange={(e) => setFormData({ ...formData, conditionnement: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="carrosserie">Carrosserie</option>
                <option value="conteneur_20">Conteneur 20'</option>
                <option value="conteneur_40">Conteneur 40'</option>
                <option value="vrac_solide">Vrac solide</option>
                <option value="vrac_liquide">Vrac liquide</option>
              </select>
            </div>

            {/* Urgence */}
            <div className="mb-2.5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Niveau d'urgence</label>
              <select
                value={formData.urgence}
                onChange={(e) => setFormData({ ...formData, urgence: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="normal">Normal</option>
                <option value="24h">Urgent (24h)</option>
                <option value="same_day">Très urgent (même jour)</option>
              </select>
            </div>

            {/* Profil client */}
            <div className="mb-2.5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Profil client</label>
              <select
                value={formData.profil_client}
                onChange={(e) => setFormData({ ...formData, profil_client: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="standard">Standard</option>
                <option value="conquete">Conquête (nouveau)</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            {/* Retour vide */}
            <div className="mb-4 flex items-center">
              <input
                type="checkbox"
                checked={formData.retour_vide}
                onChange={(e) => setFormData({ ...formData, retour_vide: e.target.checked })}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <label className="ml-2 text-sm text-gray-700">Retour à vide (+10%)</label>
            </div>

            <hr className="my-4" />

            {/* Marge minimale */}
            <div className="mb-2.5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marge minimale: <strong>{formData.marge_minimale}%</strong>
              </label>
              <input
                type="range"
                value={formData.marge_minimale}
                onChange={(e) => setFormData({ ...formData, marge_minimale: parseInt(e.target.value) })}
                min="10"
                max="40"
                step="1"
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>10%</span>
                <span>22%</span>
                <span>40%</span>
              </div>
            </div>

            {/* Bouton calcul */}
            <button
              onClick={handleCalculate}
              disabled={loading || !formData.ville_depart || !formData.ville_destination}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Calcul en cours...
                </>
              ) : (
                <>
                  <Calculator className="w-5 h-5" />
                  Calculer le tarif
                </>
              )}
            </button>
          </div>

          {/* Colonne Droite : Carte + Résultats (2/3) */}
          <div className="lg:col-span-2 space-y-4 overflow-y-auto">
            {/* Carte Interactive - Toujours visible si origine et destination sélectionnées */}
            {originLocation && destinationLocation && (
              <div className="bg-white rounded-lg shadow-md p-3">
                <MultiProviderMap
                  origin={originLocation}
                  destination={destinationLocation}
                  routeCoordinates={routeCoordinates}
                  routeData={calculatedDistance}
                  height={350}
                />
              </div>
            )}

            {/* Résultats de la simulation */}
            {result && result.success ? (
              <div className="space-y-3">
                {/* P_tkm */}
                <div className="bg-white rounded-lg shadow p-3">
                  <h2 className="text-base font-bold mb-2">📊 Prix par Tonne-Kilomètre (P_tkm)</h2>
                  <div className="flex items-center gap-3">
                    <div className="text-2xl font-bold text-emerald-600">{result.p_tkm} FCFA/t·km</div>
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                      {result.details_calcul.route_type}
                    </span>
                  </div>
                  <p className="text-gray-600 mt-2">{formatCurrency(result.tonne_km)} tonne-kilomètres</p>
                </div>

                {/* Tarifs client */}
                <div className="bg-white rounded-lg shadow p-3">
                  <h2 className="text-base font-bold mb-2">💰 Tarifs Client</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div className="bg-green-50 p-2 rounded-lg text-center">
                      <p className="text-xs text-gray-600 mb-1">Bas</p>
                      <p className="text-base font-bold text-green-700">{formatCurrency(result.tarifs_client.bas)}</p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-lg text-center">
                      <p className="text-xs text-gray-600 mb-1">Moyen</p>
                      <p className="text-base font-bold text-blue-700">{formatCurrency(result.tarifs_client.moyen)}</p>
                    </div>
                    <div className="bg-yellow-50 p-2 rounded-lg text-center">
                      <p className="text-xs text-gray-600 mb-1">Haut</p>
                      <p className="text-base font-bold text-yellow-700">{formatCurrency(result.tarifs_client.haut)}</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded-lg text-center">
                      <p className="text-xs text-gray-600 mb-1">⭐ Recommandé</p>
                      <p className="text-base font-bold text-purple-700">{formatCurrency(result.tarifs_client.recommande)}</p>
                    </div>
                  </div>
                </div>

                {/* Zone transporteur */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Truck className="w-6 h-6 text-gray-700" />
                    <h2 className="text-xl font-bold">Zone Transporteur (Négociation)</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-green-100 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">🟢 Ouverture (85%)</p>
                      <p className="text-xl font-bold text-green-700">{formatCurrency(result.zone_transporteur.ouverture)} FCFA</p>
                    </div>
                    <div className="bg-yellow-100 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">🟡 Cible (93%)</p>
                      <p className="text-xl font-bold text-yellow-700">{formatCurrency(result.zone_transporteur.cible)} FCFA</p>
                    </div>
                    <div className="bg-red-100 p-4 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">🔴 Plafond (100%)</p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(result.zone_transporteur.plafond)} FCFA</p>
                    </div>
                  </div>
                  <p className="text-gray-600 mt-3">Marge réelle: <strong>{result.marge_reelle_pct}%</strong></p>
                </div>

                {/* Comparaison historique */}
                {result.comparaison_historique && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-6 h-6 text-gray-700" />
                      <h2 className="text-xl font-bold">Comparaison Historique</h2>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg mb-3">
                      <p className="text-sm text-blue-700">
                        Basé sur {result.comparaison_historique.operations_similaires} opération(s) similaire(s)
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Tarif moyen historique</p>
                        <p className="text-2xl font-bold">{formatCurrency(result.comparaison_historique.tarif_moyen_historique)} FCFA</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Écart vs. historique</p>
                        <p className={`text-2xl font-bold ${result.comparaison_historique.ecart_vs_historique_pct > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {result.comparaison_historique.ecart_vs_historique_pct > 0 ? '+' : ''}
                          {result.comparaison_historique.ecart_vs_historique_pct}%
                        </p>
                      </div>
                    </div>
                    <span className={`inline-block mt-3 px-3 py-1 rounded-full text-sm ${result.comparaison_historique.alignement === 'conforme' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      Alignement: {result.comparaison_historique.alignement}
                    </span>
                  </div>
                )}
              </div>
            ) : !originLocation || !destinationLocation ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <Info className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Sélectionnez une origine et une destination pour voir la carte et calculer le tarif</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationOperateur;